import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'env/environment';

export interface Coordinates {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {

  constructor(private http: HttpClient) {}

  /**
   * Attempts to get the user's current coordinates using the Geolocation API.
   * Falls back to an IP based lookup when geolocation isn't available or fails.
   */
  async getCurrentPosition(options: PositionOptions = { timeout: 10000, enableHighAccuracy: false }): Promise<Coordinates> {
    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });
        return { latitude: position.coords.latitude, longitude: position.coords.longitude };
      } catch (err) {
        console.warn('Geolocation lookup failed, attempting IP based lookup:', err);
      }
    } else {
      console.warn('Navigator geolocation not supported, attempting IP based lookup.');
    }

    // Fallback: approximate location using IP based service
    try {
      const data: any = await this.http.get('https://ipapi.co/json/').toPromise();
      if (data?.latitude != null && data?.longitude != null) {
        return { latitude: data.latitude, longitude: data.longitude };
      }
    } catch (err) {
      console.error('IP based location lookup failed:', err);
    }

    throw new Error('Unable to determine current location.');
  }

  getCoordinates(place: string): Promise<[number, number]> {
    const encoded = encodeURIComponent(place);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encoded}&key=${environment.googlePlacesKey}`;

    return this.http.get<any>(url).toPromise().then(res => {
      if (res.status === 'OK' && res.results.length > 0) {
        const location = res.results[0].geometry.location;
        return [location.lng, location.lat]; // [lng, lat]
      } else {
        throw new Error(`Could not geocode place: ${place}`);
      }
    });
  }

  /**
   * Uses the Google Geocoding API to reverse geocode latitude and longitude into a location.
   * Returns an object with the formatted location and its components, or null if unsuccessful.
   */
  async reverseGeocode(lat: number, lng: number): Promise<{ location: string; city: string; state: string; country: string } | null> {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${environment.googlePlacesKey}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'OK' && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
        let city = '';
        let state = '';
        let country = '';

        for (const component of addressComponents) {
          if (component.types.includes('locality')) {
            city = component.short_name;
          } else if (component.types.includes('administrative_area_level_1')) {
            state = component.short_name;
          } else if (component.types.includes('country')) {
            country = component.short_name;
          }
        }

        // For now, only return US addresses.
        if (city && state && country === 'US') {
          return {
            location: `${city}, ${state}, US`,
            city,
            state,
            country,
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error during reverse geocoding:', error);
      return null;
    }
  }

  /**
   * Determines whether two sets of coordinates are within the given tolerance.
   */
  isSameGeneralArea(
    currentLat: number,
    currentLng: number,
    cachedLat: number,
    cachedLng: number,
    tolerance: number = 0.1
  ): boolean {
    if (cachedLat == null || cachedLng == null) {
      return false;
    }
    return Math.abs(currentLat - cachedLat) < tolerance &&
           Math.abs(currentLng - cachedLng) < tolerance;
  }
}
